import { useState, useEffect } from 'react'
import { VisibilityState } from '@tanstack/react-table'

/**
 * Custom hook to persist column visibility state in localStorage
 * @param storageKey - Unique key for localStorage (e.g., 'cases-table', 'users-table')
 * @param initialState - Initial visibility state
 * @returns [columnVisibility, setColumnVisibility]
 */
export function useColumnVisibility(
  storageKey: string,
  initialState: VisibilityState = {}
): [VisibilityState, React.Dispatch<React.SetStateAction<VisibilityState>>] {
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    // Try to load from localStorage on initial render
    try {
      const stored = localStorage.getItem(`columnVisibility-${storageKey}`)
      if (stored) {
        return JSON.parse(stored) as VisibilityState
      }
    } catch (error) {
      console.error('Error loading column visibility from localStorage:', error)
    }
    return initialState
  })

  // Save to localStorage whenever columnVisibility changes
  useEffect(() => {
    try {
      localStorage.setItem(
        `columnVisibility-${storageKey}`,
        JSON.stringify(columnVisibility)
      )
    } catch (error) {
      console.error('Error saving column visibility to localStorage:', error)
    }
  }, [columnVisibility, storageKey])

  return [columnVisibility, setColumnVisibility]
}
